import React, { useState, useMemo, useEffect } from "react";
import { Button, Table, message } from "antd";
import { motion } from "framer-motion";
import { ChevronDown, Trash2 } from "lucide-react";
import useAxiosInstance from "../../../../lib/useAxiosInstance";

const MIN_HEIGHT = 260;

const Summary = ({ data = [], onClose, surveyId }) => {
  const axiosInstance = useAxiosInstance();

  const [height, setHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight * 0.5 : 400
  );

  const [isResizing, setIsResizing] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(0);

  const [hiddenHeadings, setHiddenHeadings] = useState([]);
  const [headingToDelete, setHeadingToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* ---------------- Resize ---------------- */
  const handleMouseDownResize = (e) => {
    e.preventDefault();
    setIsResizing(true);
    setStartY(e.clientY);
    setStartHeight(height);
  };

  useEffect(() => {
    if (!isResizing) return;

    const move = (e) => {
      const delta = startY - e.clientY;
      const max = window.innerHeight - 64;
      setHeight(Math.min(Math.max(startHeight + delta, MIN_HEIGHT), max));
    };

    const up = () => setIsResizing(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [isResizing, startY, startHeight]);

  /* ---------------- Pivot Data ---------------- */
  const { rows, headings } = useMemo(() => {
    if (!data.length) return { rows: [], headings: [] };

    const rowMap = new Map();
    const headingSet = new Set();

    data.forEach((item) => {
      headingSet.add(item.heading);
      const key = [item.constituency, item.village, item.booth].join("||");

      if (!rowMap.has(key)) {
        rowMap.set(key, {
          constituency: item.constituency || "",
          village: item.village || "",
          booth: item.booth || "",
        });
      }

      rowMap.get(key)[item.heading] = item.text_value;
    });

    return {
      rows: Array.from(rowMap.values()),
      headings: Array.from(headingSet),
    };
  }, [data]);

  const sample = rows[0] || {};
  const hasBlock = "constituency" in sample;
  const hasVillage = sample.village !== "";
  const hasBooth = sample.booth !== "";

  /* ---------------- DELETE FLOW ---------------- */
  const confirmDelete = async () => {
    const heading = headingToDelete;
    setHeadingToDelete(null);
    setIsDeleting(true);

    // Optimistic UI update
    setHiddenHeadings((prev) => [...prev, heading]);

    try {
      await axiosInstance.delete("/api/surveyAnalytics/summary", {
        data: { heading, surveyId },
      });

      message.success(`"${heading}" deleted successfully`);
    } catch (err) {
      // Rollback on failure
      setHiddenHeadings((prev) => prev.filter((h) => h !== heading));
      message.error("Delete failed. Column restored.");
    } finally {
      setIsDeleting(false);
    }
  };

  /* ---------------- Columns ---------------- */
  const columns = useMemo(() => {
    const cols = [];

    if (hasBlock) cols.push({ title: "Constituency", dataIndex: "constituency" });
    if (hasVillage) cols.push({ title: "Village", dataIndex: "village" });
    if (hasBooth) cols.push({ title: "Booth", dataIndex: "booth" });

    headings
      .filter((h) => !hiddenHeadings.includes(h))
      .forEach((heading) => {
        cols.push({
          title: (
            <div className="flex items-center justify-between gap-2">
              <span className="truncate">{heading}</span>
            </div>
          ),
          dataIndex: heading,
          key: heading,
        });
      });

    return cols;
  }, [headings, hiddenHeadings, hasBlock, hasVillage, hasBooth]);

  /* ---------------- Render ---------------- */
  return (
    <>
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl p-4 z-[50000] flex flex-col"
        style={{ height }}
      >
        {/* Drag Handle */}
        <div
          className="flex justify-center cursor-ns-resize"
          onMouseDown={handleMouseDownResize}
        >
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-[#123865]">Summary</h3>
          <div className="flex gap-2">
            <Button
              type="text"
              icon={<ChevronDown size={22} />}
              onClick={onClose}
            />
          </div>
        </div>

        <Table
          dataSource={rows}
          columns={columns}
          pagination={{ pageSize: 20 }}
          size="small"
          rowKey={(_, i) => i}
        />
      </motion.div>
    </>
  );
};

export default Summary;
